import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Navigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { DOCS_BASE } from "./nav";
import { siteUrl } from "./shared/paths";
import { usePage, useSite } from "./data/useDocsData";
import { Header } from "./layout/Header";
import { Sidebar } from "./layout/Sidebar";
import { Toc } from "./layout/Toc";
import { PageFooter, PageHeader } from "./layout/PageChrome";
import { SearchDialog } from "./layout/SearchDialog";
import { Head } from "./layout/Head";
import { OWN_RIGHT_COLUMN, PageBody } from "./pages/PageBody";
import { COPY } from "./copy";
import { DocsThemeProvider } from "./layout/theme";

/**
 * The developer docs (design §0): every path under `DOCS_BASE` is one page of the generated site.
 * The site reads the build's `site.json` (tabs, menus, page index, redirects) and the open page's
 * data file; it never calls the API.
 *
 * `data-docs-ready` marks the moment the page's content is on screen — the prerender step
 * (`scripts/prerender-docs.mjs`) waits for it before saving the HTML.
 *
 * The whole site is this one component under a router (the app's `/docs/*` route is its preview,
 * K104); it imports nothing from outside `src/docs-site/` (`importBoundary.test.ts`).
 */

/** "/docs/task-apis/limits/" → "/task-apis/limits"; the docs root is "/". */
export const docsPath = (pathname: string): string => {
  const rest = pathname.startsWith(DOCS_BASE) ? pathname.slice(DOCS_BASE.length) : pathname;
  const trimmed = rest.replace(/\/+$/, "");

  return trimmed === "" ? "/" : decodeURIComponent(trimmed);
};

const Message = ({ text }: { text: string }) => <p className="py-24 text-center text-bodyMedium text-muted-foreground">{text}</p>;

const DocsSite = () => {
  const location = useLocation();
  const path = docsPath(location.pathname);
  const site = useSite();
  const siteData = site.status === "ready" ? site.data : null;
  const entry = siteData?.pages[path] ?? null;
  const page = usePage(entry ? path : null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // A page opened at an anchor scrolls to it once the content is there.
  useEffect(() => {
    if (page.status !== "ready") return;
    if (location.hash) document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [page.status, path, location.hash]);

  if (site.status === "loading") return <Message text={COPY.loading} />;
  if (!siteData) return <Message text={COPY.loadFailed} />;
  const redirect = siteData.redirects[path];
  if (!entry && redirect) return <Navigate to={siteUrl(redirect)} replace />;

  const ready = page.status === "ready";
  const showToc = ready && page.data.mode !== "wide" && !OWN_RIGHT_COLUMN.has(page.data.kind) && page.data.toc.length > 0;

  return (
    <div className="min-h-screen bg-background font-workSans text-foreground">
      <Header site={siteData} activeTab={entry?.tab} onSearch={() => setSearchOpen(true)} onMenu={() => setMenuOpen(true)} />
      <div className="mx-auto flex max-w-[90rem] gap-8 px-4 clg:px-6">
        <aside className="hidden w-64 shrink-0 clg:block">
          <div className="sticky top-28 max-h-[calc(100vh-7rem)] overflow-y-auto py-6 pr-2">{entry ? <Sidebar site={siteData} tab={entry.tab} path={path} onNavigate={() => undefined} /> : null}</div>
        </aside>
        <main className="min-w-0 flex-1 py-8" data-docs-ready={ready || !entry ? "true" : undefined}>
          {!entry ? <Message text={COPY.notFoundTitle} /> : null}
          {entry && page.status === "loading" ? <Message text={COPY.loading} /> : null}
          {entry && (page.status === "error" || page.status === "missing") ? <Message text={COPY.loadFailed} /> : null}
          {entry && ready ? (
            <article data-pagefind-body data-pagefind-filter={`tab:${siteData.tabs.find((t) => t.id === entry.tab)?.label ?? ""}`}>
              <Head entry={entry} />
              <PageHeader entry={entry} />
              <PageBody page={page.data} />
              <PageFooter entry={entry} site={siteData} />
            </article>
          ) : null}
        </main>
        <aside className={clsx("hidden w-56 shrink-0", showToc && "lg:block")}>
          <div className="sticky top-28 py-8">{showToc && ready ? <Toc items={page.data.toc} /> : null}</div>
        </aside>
      </div>
      {menuOpen && entry ? (
        <div className="fixed inset-0 z-40 bg-trueBlack/50 clg:hidden" onMouseDown={() => setMenuOpen(false)}>
          <div className="h-full w-80 max-w-full overflow-y-auto bg-background p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <span className="font-semibold">{COPY.navigation}</span>
              <button type="button" onClick={() => setMenuOpen(false)} aria-label={COPY.closeNavigation}>
                <X size={18} />
              </button>
            </div>
            <Sidebar site={siteData} tab={entry.tab} path={path} onNavigate={() => setMenuOpen(false)} />
          </div>
        </div>
      ) : null}
      <SearchDialog site={siteData} open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

const DocsApp = () => (
  <DocsThemeProvider>
    <DocsSite />
  </DocsThemeProvider>
);

export default DocsApp;
